/**
 * ExportMenuButton — Botão reaproveitável de exportação de dados
 * -----------------------------------------------------------------------------
 * Abre o modal genérico de exportação (ExportDialog) já apontando para a fonte
 * do módulo. Some automaticamente quando o perfil não tem nenhuma forma de
 * entrega liberada (arquivo ou e-mail).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useState } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ExportDialog } from './ExportDialog';
import { useMyExportPermissions } from '@/hooks/useExportPermissions';

interface ExportMenuButtonProps {
  /** Id da fonte registrada (ex.: 'crm_deals') */
  sourceId: string;
  /** Parâmetros da tela (filtros/ordenação) */
  params: unknown;
  /** Sufixo do nome do arquivo (ex.: nome da fase) */
  fileNameHint?: string;
  /** Variação visual: 'icon' (cabeçalho de coluna) ou 'button' (listagem) */
  variant?: 'icon' | 'button';
  /** Rótulo do botão na variação 'button' */
  label?: string;
}

export function ExportMenuButton({
  sourceId,
  params,
  fileNameHint,
  variant = 'icon',
  label = 'Exportar',
}: ExportMenuButtonProps) {
  const [aberto, setAberto] = useState(false);
  const { getPermission, loading } = useMyExportPermissions();
  const permissao = getPermission(sourceId);

  // Sem nenhuma forma de entrega liberada, o botão não aparece
  if (loading || (!permissao.canDownload && !permissao.canEmail)) return null;

  return (
    <>
      {variant === 'icon' ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={() => setAberto(true)}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Exportar dados</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAberto(true)}>
          <Download className="mr-2 h-4 w-4" />
          {label}
        </Button>
      )}

      <ExportDialog
        open={aberto}
        onOpenChange={setAberto}
        sourceId={sourceId}
        params={params}
        fileNameHint={fileNameHint}
      />
    </>
  );
}
